import { KeyResult } from "@/types";
import { useKeyResults, krListParams } from "@/hooks/useKeyResults";
import { KeyResultCard } from "./KeyResultCard";
import { Skeleton } from "@/components/ui/skeleton";
import { TrendingUp } from "lucide-react";
import { Draggable, Droppable } from "@hello-pangea/dnd";
import { krDroppableId, TREE_ROW_COLS } from "./okr-tree-constants";

interface KeyResultListProps {
  objectivePid: string;
  onEditKeyResult?: (keyResult: KeyResult) => void;
  onDeleteKeyResult?: (keyResult: KeyResult) => void;
  /** Requires an enclosing DragDropContext (ObjectiveList provides it). */
  isDragEnabled?: boolean;
  compact?: boolean;
  canManage?: boolean;
}

export function KeyResultList({
  objectivePid,
  onEditKeyResult,
  onDeleteKeyResult,
  isDragEnabled = false,
  compact,
  canManage = false,
}: KeyResultListProps) {
  const { data, isLoading } = useKeyResults(krListParams(objectivePid));
  const keyResults = data?.data ?? [];
  // A reorder persists a permutation of the rows in hand, so it is only correct on the whole list;
  // `krListParams` caps how many arrive.
  const canDrag = isDragEnabled && (data?.total ?? 0) <= keyResults.length;

  if (isLoading) {
    return (
      <div>
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className="grid gap-3 px-6 min-h-[52px] items-center border-b border-border/40" style={TREE_ROW_COLS}>
            <div className="flex items-center gap-2 pl-[62px]"><Skeleton className="h-3 w-48" /></div>
            <Skeleton className="h-5 w-16 rounded-full" />
            <Skeleton className="h-1 w-full rounded-full" />
            <Skeleton className="h-4 w-20" />
          </div>
        ))}
      </div>
    );
  }

  if (keyResults.length === 0) {
    return (
      <div className="flex items-center gap-2 px-4 pl-[62px] min-h-[36px] border-b border-border/40 text-[12px] text-muted-foreground">
        <TrendingUp className="h-3.5 w-3.5 flex-shrink-0" />
        No key results yet
      </div>
    );
  }

  if (!canDrag) {
    return (
      <div>
        {keyResults.map((kr) => (
          <KeyResultCard key={kr.pid} keyResult={kr} onEdit={onEditKeyResult} onDelete={onDeleteKeyResult} compact={compact} canManage={canManage} />
        ))}
      </div>
    );
  }

  return (
    <Droppable droppableId={krDroppableId(objectivePid)} type="KR">
      {(provided) => (
        <div ref={provided.innerRef} {...provided.droppableProps}>
          {keyResults.map((kr, index) => (
            <Draggable key={kr.pid} draggableId={kr.pid} index={index}>
              {(dragProvided) => (
                <div ref={dragProvided.innerRef} {...dragProvided.draggableProps}>
                  <KeyResultCard
                    keyResult={kr}
                    onEdit={onEditKeyResult}
                    onDelete={onDeleteKeyResult}
                    dragHandleProps={dragProvided.dragHandleProps}
                    compact={compact}
                    canManage={canManage}
                  />
                </div>
              )}
            </Draggable>
          ))}
          {provided.placeholder}
        </div>
      )}
    </Droppable>
  );
}
